import React, { useState, useEffect } from 'react';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSelector, useDispatch } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { getFirstKey } from 'global/utils';
import { get, set, size } from 'global/lib/lodash';
import { Button, List, Icon } from '@mui/material';
import TaskListItem from './TaskListItem';
import useStyles from './TaskList.styles';
import TaskCreate from '../TaskCreate';
import { useHistory } from 'react-router-dom';
import setFilter from 'redux/actions/setFilter';
import { userHasPermissions } from 'redux/actions/userLogin';
import { Access } from 'global/components/droprealms';
import Modal from 'global/components/modal';
import Styles from 'global/styles';
import sendData from 'redux/actions/sendData';
import { setTaskList } from 'redux/actions/view';
import { withRouter } from 'react-router-dom';
import setUserRealm from 'redux/actions/userRealm';
import withSearchBar from 'global/components/withSearchBar';
import { Text } from 'global/components/text';
import clsx from 'clsx';
import PerformTasksFilters from '../Filters';
import { useScreen } from 'global/styles';
import { FilterChips } from 'global/components/filterchips';
import { wait } from '../../../global/utils';

const Includes = ['realm', 'realm.parent', 'vendor'];
const pageSize = 25;

export default withRouter((props) => {
  const include = Includes.join(',');
  const filter = useSelector((state) => get(state, 'filter.tasks.list'));
  const hasRealm = useSelector((state) => get(state, 'auth.realm'));
  const [hasChangedFilter, setHasChangedFilter] = useState(false);
  const dispatch = useDispatch();
  const styles = Styles();

  const { isMobile } = useScreen();

  const filterProps = () => {
    return buildFilters(filter, {
      includeRealm: true,
      convertName: { searchstring: 'name', realm_type: 'tier' },
      dontFilter: ['content'],
      overrideFilter: (_filtObj) => {
        const filtObj = { ..._filtObj };
        if (filtObj.status) {
          set(filtObj, filtObj.status, true);
          delete filtObj.status;
        }
        if (filtObj.vendor_id === 0) {
          delete filtObj.vendor_id;
        }
        delete filtObj.age;
        return filtObj;
      },
      overrideSort: (sort) => {
        if (sort.indexOf('name') > -1) {
          return ['name'];
        }
        if (sort.indexOf('count') > -1) {
          return ['-total'];
        }
        return sort;
      },
    });
  };

  const accessContent = () => {
    return (
      <div className={styles.formContainer}>
        <Access
          openToSelected={true}
          onlyTiers={['division', 'community', 'lot']}
          currentRealm={hasRealm}
          canAdd={true}
          singleSelect={true}
          onSelectedChange={(s) => closeDialog(getFirstKey(s))}
          selectTitle="Choose a location for the Task"
        />
      </div>
    );
  };
  const [urlParams, setUrlParams] = useState({ build: ['processedTaskCountValues'], waitFor: null, params: { ...filterProps(), page: { size: pageSize, number: 1 }, include } });
  const [realmDialog, setRealmDialog] = useState({
    open: false,
    title: 'Choose Location',
    keepMounted: true,
    onClose: () => setRealmDialog({ ...realmDialog, open: false }),
    content: accessContent,
  });
  const [createTask, setCreateTask] = useState({ open: false });

  const classes = useStyles();
  const history = useHistory();

  useEffect(() => {
    if (!hasChangedFilter && filter.searchstring) {
      setHasChangedFilter(true);
      dispatch(setFilter('tasks', 'list', set({ ...filter }, '_sort.sortBy', 'name')));
    }
  }, [filter.searchstring]);

  useEffect(() => {
    setUrlParams({
      ...urlParams,
      useEffect: true,
      waitFor: undefined,
      params: {
        ...urlParams.params,
        ...{ page: { size: pageSize, number: 1 }, include },
        ...filterProps(),
      },
    });
  }, [filter, hasRealm]);

  const tasks = useRetrieveData({ url: '/processed-task-count-values' }, urlParams);

  const openCreateTask = (realm) => {
    if (realm) {
      dispatch(setUserRealm(realm));
      setCreateTask({
        open: true,
        realmItem: realm
          ? {
              realm,
              realmName: get(realm, 'name', ''),
            }
          : null,
        onClose: async (task) => {
          if (task && realm) {
            await wait(2000); //seems to be a race condition on the backend, as this hasn't finished processing or something before it shows the list again
            const da = await sendData({ url: '/processed-task-count-values' }, { normalize: { build: ['processedTaskCountValues'] }, params: { filter: { realmid: realm.id } } });
            const id = get(da, '0.id');
            setCreateTask({ open: false });
            if (id) {
              history.push(`/tasks/list/${id}`);
            }
          } else {
            setCreateTask({ open: false });
          }
        },
      });
    }
  };

  const closeDialog = (realm) => {
    openCreateTask(realm);
    setRealmDialog({
      ...realmDialog,
      open: false,
    });
  };

  useEffect(() => {
    dispatch(setTaskList(tasks.data));
    if (size(tasks.data) === 1 && get(tasks, 'data.0.realm.id') === get(hasRealm, 'id') && !get(createTask, 'open')) {
      props.history.push(`/tasks/list/${get(tasks, 'data.0.id')}`);
    }
  }, [tasks.data]);

  useEffect(() => {
    setRealmDialog({
      ...realmDialog,
      content: accessContent,
    });
  }, [hasRealm]);

  const openRealm = () => {
    if (hasRealm && hasRealm.tier === 'lot') {
      return openCreateTask(hasRealm);
    }
    setRealmDialog({
      ...realmDialog,
      content: accessContent,
      open: true,
    });
  };

  const lastPage = get(tasks.meta, 'page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setUrlParams({
        ...urlParams,
        params: {
          ...urlParams.params,
          page: { size: pageSize, number: newPage },
        },
      });
    }
  };

  return (
    <>
      <div className={clsx(classes.row, classes.padLeft)}>
        <div>
          {!isMobile && <Text type={'headline_medium'}>
            Tasks by Location
          </Text>}
          {isMobile ? <PerformTasksFilters {...props} /> : <FilterChips id={'tasks.list'} />}
        </div>
        <Button disabled={!userHasPermissions('createTasks')} className={classes.createButton} variant="contained" color="secondary" onClick={openRealm} startIcon={<Icon>add</Icon>}>
          CREATE NEW
        </Button>
      </div>

      <ListWithPages
        handlePageChange={(np) => updatePage(np)}
        currentPage={get(urlParams, 'params.page.number', 1)}
        lastPage={lastPage}
        data={tasks.data}
        loading={tasks.loading}
        header={null}
        {...props}
      />
      <Modal {...realmDialog} />
      <TaskCreate {...createTask} />
    </>
  );
});

const ListWithPages = withSearchBar(withInfiniteScroll(TaskListItem, List));
